import { AllSameKeyValuesEnforcer, getTwoDeepValue, JustTime, removeDiacritics } from './helpers';
import { TableColumn } from './table.data';
import { publishTableColumns, shareTableColumns, briefTableColumns, sponsorColumns, sessionColumns, performerTableColumns, sponsorSessionColumns, timeLogColumns, taskTimeColumns, exhibitorColumns, eventListTableColumns, taskTableColumns, exhibitorStandColumns, excludedPerformerClipboardColumns, excludedExhibitorClipboardColumns, excludedSponsorClipboardColumns, templateTableColumns, assetColumns, excludedAssetClipboardColumns, resourceColumns, excludedResourceClipboardColumns, quoteColumns, budgetColumns, programColumns, appTableColumns, attendeeTableColumns, excludedAttendeeClipboardColumns } from './table-column-types';
import { PublishStatus } from '../../../globals/store/share.data';
import { tableLightColors, TableLightColors, minContents } from '../../../globals/store/table-helpers.data';
import { Task, taskStatusOrders } from '../../../globals/store/tasks.data';
import { CustomQuestion } from '../../../globals/store/events.data';
import { makeMapFromListOfObjectsAndKey, makeMapFromListOfObjectsAndKeyToProperty } from 'globals/helpers/helpers';
export { tableLightColors, TableLightColors, minContents }

export interface TableTemplateIds { tableImage, tableTextLights, tableUpdatedText, tableLightsMap, tableDate, tableText, tableLabels, tableUser, tableWorker, tableEmail, tableLink, tableSocial, boldText, tableHHMM, iconText, tableAccount, tableCheck, tableFlag, tableComments, tableFiles, tableEntity, tableStars, taskStatus }

export const tableTemplateIds: AllSameKeyValuesEnforcer<TableTemplateIds> = { boldText: 'boldText', tableImage: 'tableImage', tableTextLights: 'tableTextLights', tableDate: 'tableDate', tableText: 'tableText', tableLabels: 'tableLabels', tableUser: 'tableUser', tableWorker: 'tableWorker', tableLightsMap: 'tableLightsMap', tableEmail: 'tableEmail', tableUpdatedText: 'tableUpdatedText', tableLink: 'tableLink', tableSocial: 'tableSocial', tableHHMM: 'tableHHMM', iconText: 'iconText', tableAccount: 'tableAccount', tableCheck: 'tableCheck', tableFlag: 'tableFlag', tableComments: 'tableComments', tableFiles: 'tableFiles', tableEntity: 'tableEntity', tableStars: 'tableStars', taskStatus: 'taskStatus' }

export const colorsByPublishStatus: { [key in PublishStatus]: string } = { published: tableLightColors.green, unpublished: tableLightColors.red, 'never published': tableLightColors.gray }

const compareNums = (a, b) => ({ j: Number(a), k: Number(b) })
const compareText = (a, b) => {
  const normalizedA = removeDiacritics(a).trim().toLowerCase()
  const normalizedB = removeDiacritics(b).trim().toLowerCase()
  return { j: normalizedA, k: normalizedB }
}


const compareHHMM = (a: JustTime, b: JustTime) => ({ j: a.h*60 + a.m, k: b.h*60 + b.m })
const compareOthers = (a, b) => ({ j: a, k: b })

const getPrevValue = (a, b, prev, mult) => {
  if (prev[a.id] === undefined) return -1 // move new items to end of list, after other sorting
  if (prev[b.id] === undefined) return 1 // move new items to end of list, after other sorting
  return prev[a.id] < prev[b.id] ? -1 : 1
}

const twoDeepSorter = method => (prop1, prop2) => ({ a, b, mult, prev }) => {
  const x = getTwoDeepValue(prop1, prop2, a)
  const y = getTwoDeepValue(prop1, prop2, b)
  const noX = x === undefined
  const noY = y === undefined

  if (noX && noY) return getPrevValue(a, b, prev, mult)
  if (noX && !noY) return mult
  if (noY && !noX) return -mult
  const { j, k } = method(x, y)
  return j === k ? getPrevValue(a, b, prev, mult) : j < k ? mult : -mult
}

export const twoDeepTextSorter = twoDeepSorter(compareText)
export const taskStatusSorter = ({ a, b, mult }) => {
  const aStatus = a.status.task.status
  const bStatus = b.status.task.status
  // console.log({ aStatus, bStatus })
  return (taskStatusOrders[aStatus] < taskStatusOrders[bStatus] ? -1 : 1) * mult
}


// twoDeepTextOrNumberSorter  is the best can do when trying to sort text as numbers but only sometimes 
// (created for the exhibitor stands table for sorting stand "number")
const twoDeepTextOrNumberSorter = (prop1, prop2) => ({ a, b, mult, prev }) => {
  const x = getTwoDeepValue(prop1, prop2, a)
  const y = getTwoDeepValue(prop1, prop2, b)
  const noX = x === undefined
  const noY = y === undefined
  if (noX && noY) return getPrevValue(a, b, prev, mult)
  if (noX && !noY) return mult
  if (noY && !noX) return -mult

  const method = isNaN(x) || isNaN(y) ? compareText : compareNums
  const { j, k } = method(x, y)
  return j === k ? getPrevValue(a, b, prev, mult) : j < k ? mult : -mult
}

const twoDeepHHMMSorter = twoDeepSorter(compareHHMM)
const twoDeepNoNullSorter = twoDeepSorter(compareNums)
const arrayLengthSorter = prop => ({ a, b, mult }) => a[prop].length <= b[prop].length ? mult : -mult
export const twoDeepSimpleSorter = twoDeepSorter(compareOthers)

export const customQuestionColumnFromQuestion = (q: CustomQuestion): TableColumn => ({
  title: q.question,
  id: q.id,
  templateId: q.type === 'link' ? tableTemplateIds.tableLink : q.type === 'stars' ? tableTemplateIds.tableStars : tableTemplateIds.tableText,
  sortingFunction: q.type === 'link' ? twoDeepTextSorter(q.id, 'url') : q.type === 'stars' ? twoDeepSimpleSorter(q.id, 'stars') : twoDeepTextSorter(q.id, 'text')
})

// { name, responsibility, visibility, estimatedTime, loggedTime, difference }
export const allDefaultTaskTimeColumns: TableColumn[] = [
  {
    title: 'Task',
    id: taskTimeColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(taskTimeColumns.name, 'text'),
  }, {
    title: 'Responsibility',
    id: taskTimeColumns.responsibility,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(taskTimeColumns.responsibility, 'name'),
  }, {
    title: 'Visibility',
    id: taskTimeColumns.visibility,
    templateId: tableTemplateIds.iconText,
    sortingFunction: twoDeepNoNullSorter(taskTimeColumns.visibility, 'rank'),
  }, {
    title: 'Estimated time',
    id: taskTimeColumns.estimatedTime,
    templateId: tableTemplateIds.tableHHMM,
    sortingFunction: twoDeepHHMMSorter(taskTimeColumns.estimatedTime, 'time'),
  }, {
    title: 'Logged time',
    id: taskTimeColumns.loggedTime,
    templateId: tableTemplateIds.tableHHMM,
    sortingFunction: twoDeepHHMMSorter(taskTimeColumns.loggedTime, 'time'),
  }, {
    title: 'Difference',
    id: taskTimeColumns.difference,
    templateId: tableTemplateIds.tableHHMM,
    sortingFunction: twoDeepHHMMSorter(taskTimeColumns.difference, 'time'),
  }, 
]


// interface AppTableColumnIds { app, name, description, status, updated, created }
export const allDefaultAppColumns: TableColumn[] = [
  {
    title: 'Name',
    id: appTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(appTableColumns.name, 'text'),
  }, {
    title: 'Description',
    id: appTableColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(appTableColumns.description, 'text'),
  }, {
    title: 'Status',
    id: appTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(appTableColumns.status, 'percent'),
  }, {
    title: 'Date created',
    id: appTableColumns.created,
    templateId: tableTemplateIds.tableUpdatedText,
    sortingFunction: twoDeepSimpleSorter(appTableColumns.created, 'lastUpdated'),
  }, {
    title: 'Last updated',
    id: appTableColumns.updated,
    templateId: tableTemplateIds.tableUpdatedText,
    sortingFunction: twoDeepSimpleSorter(appTableColumns.updated, 'lastUpdated'),
  }
]
export const appTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAppColumns, 'id')



// { loggedTime, notes, loggedBy, date }
export const allDefaultTimeLogColumns: TableColumn[] = [
  {
    title: 'Logged time',
    id: timeLogColumns.loggedTime,
    templateId: tableTemplateIds.tableHHMM,
    sortingFunction: twoDeepHHMMSorter(timeLogColumns.loggedTime, 'time'),
  }, {
    title: 'Notes',
    id: timeLogColumns.notes,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(timeLogColumns.notes, 'text'),
  }, {
    title: 'Logged by',
    id: timeLogColumns.loggedBy,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(timeLogColumns.loggedBy, 'name'),
  }, {
    title: 'Date',
    id: timeLogColumns.date,
    templateId: tableTemplateIds.tableDate,
    sortingFunction: twoDeepSimpleSorter(timeLogColumns.date, 'date'),
  },
]

export const allTaskTimeLogColumns: TableColumn[] = [...allDefaultTimeLogColumns]
allTaskTimeLogColumns.unshift({
  title: 'Task',
  id: timeLogColumns.task,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(timeLogColumns.task, 'text'),
})
export const allTaskTimeLogColumnsById = makeMapFromListOfObjectsAndKey(allTaskTimeLogColumns, 'id')


export const allDefaultEventListColumns: TableColumn[] = [
  {
    title: 'Name',
    id: eventListTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(eventListTableColumns.name, 'text'),
  }, {
    title: 'Description',
    id: eventListTableColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(eventListTableColumns.description, 'text'),
  }, {
    title: 'Account',
    id: eventListTableColumns.account,
    templateId: tableTemplateIds.tableAccount,
    sortingFunction: twoDeepTextSorter(eventListTableColumns.account, 'name'),
  }, {
    title: 'Status',
    id: eventListTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(eventListTableColumns.status, 'percent'),
  }, {
    title: 'Date created',
    id: eventListTableColumns.created,
    templateId: tableTemplateIds.tableUpdatedText,
    sortingFunction: twoDeepSimpleSorter(eventListTableColumns.created, 'lastUpdated'),
  }, {
    title: 'Last published',
    id: eventListTableColumns.updated,
    templateId: tableTemplateIds.tableUpdatedText,
    sortingFunction: twoDeepSimpleSorter(eventListTableColumns.updated, 'lastUpdated'),
  },
]


export const allDefaultPublishColumns: TableColumn[] = [
  {
    title: 'Name',
    id: publishTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(publishTableColumns.name, 'text'),
  }, {
    title: 'Description',
    id: publishTableColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(publishTableColumns.description, 'text'),
  }, {
    title: 'Status',
    id: publishTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(publishTableColumns.status, 'percent'),
  }, {
    title: 'Last published',
    id: publishTableColumns.updated,
    templateId: tableTemplateIds.tableUpdatedText,
    sortingFunction: twoDeepSimpleSorter(publishTableColumns.updated, 'lastUpdated'),
  }, {
    title: 'Connected event lists',
    id: publishTableColumns.eventLists,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(publishTableColumns.eventLists, 'percent'),
  },
]

export const allDefaultShareColumns: TableColumn[] = [
  {
    title: 'Name',
    id: shareTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(shareTableColumns.name, 'text'),
  }, {
    title: 'Description',
    id: shareTableColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(shareTableColumns.description, 'text'),
  }, {
    title: 'Status',
    id: shareTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(shareTableColumns.status, 'percent'),
  }
]

export const allDefaultLogisticsShareColumns: TableColumn[] = [
  {
    title: 'Name',
    id: shareTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(shareTableColumns.name, 'text'),
  }, {
    title: 'Description',
    id: shareTableColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(shareTableColumns.description, 'text'),
  }, {
    title: 'Status',
    id: shareTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(shareTableColumns.status, 'percent'),
  }
]



export const allDefaultExhibitorBriefColumns: TableColumn[] = [
  {
    title: 'Exhibitor name',
    id: briefTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(briefTableColumns.name, 'text'),
  }, {
    title: 'Exhibitor brief URL',
    id: briefTableColumns.briefUrl,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(briefTableColumns.briefUrl, 'percent'),
  }, {
    title: 'Exhibitor status',
    id: briefTableColumns.status,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(briefTableColumns.status, 'percent'),
  }
]

export const allDefaultPerformerBriefColumns: TableColumn[] = [
  {
    title: 'Performer name',
    id: briefTableColumns.name,
    templateId: tableTemplateIds.boldText,
    sortingFunction: twoDeepTextSorter(briefTableColumns.name, 'text'),
  }, {
    title: 'Sessions',
    id: briefTableColumns.sessions,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(briefTableColumns.sessions, 'text'),
  }, {
    title: 'Session brief status',
    id: briefTableColumns.briefStatus,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(briefTableColumns.briefStatus, 'percent'),
  }, {
    title: 'Performer brief URL',
    id: briefTableColumns.briefUrl,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(briefTableColumns.briefUrl, 'percent'),
  }, {
    title: 'Terms',
    id: briefTableColumns.terms,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(briefTableColumns.terms, 'percent'),
  }
]

export const allDefaultExhibitorStandsColumns: TableColumn[] = [{
  title: 'Stand Number',
  id: exhibitorStandColumns.standNumber,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextOrNumberSorter(exhibitorStandColumns.standNumber, 'text'),
}, {
  title: 'Room/location',
  id: exhibitorStandColumns.program,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorStandColumns.program, 'text'),
}, {
  title: 'Exhibitor',
  id: exhibitorStandColumns.exhibitor,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorStandColumns.exhibitor, 'text'),
}, {
  title: 'Stand requests',
  id: exhibitorStandColumns.standRequests,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorStandColumns.standRequests, 'percent'),
  notNested: true,
}, {
  title: 'Stand size',
  id: exhibitorStandColumns.standSize,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(exhibitorStandColumns.standSize, 'rank'), // sort by "rank" of Exhibitor type
}, {
  title: 'Stand notes',
  id: exhibitorStandColumns.standNotes,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorStandColumns.standNotes, 'text'),
}, {
  title: 'Stand Assets',
  id: exhibitorStandColumns.standAssets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorStandColumns.standAssets, 'percent'),
}]

export const allDefaultExhibitorsColumns: TableColumn[] = [{
  title: 'Exhibitor name',
  id: exhibitorColumns.name,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.name, 'text'),
}, {
  title: 'Exhibitor type',
  id: exhibitorColumns.exhibitorType,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(exhibitorColumns.exhibitorType, 'rank'), // sort by "rank" of sponsor type
}, {
  title: 'Stars',
  id: exhibitorColumns.stars,
  templateId: tableTemplateIds.tableStars,
  sortingFunction: twoDeepSimpleSorter(exhibitorColumns.stars, 'stars'),
}, {
  title: 'Exhibitor status',
  id: exhibitorColumns.confirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.confirmed, 'percent'),
}, {
  title: 'Stands',
  id: exhibitorColumns.stands,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
}, {
  title: 'Exhibitor assets',
  id: exhibitorColumns.assets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.assets, 'percent'),
  notNested: true,
}, {
  title: 'Stand assets',
  id: exhibitorColumns.standAssets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.standAssets, 'percent'),
  notNested: true,
}, /* , {
  title: 'Stand size',
  id: exhibitorColumns.standSize,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(exhibitorColumns.standSize, 'rank'), // sort by "rank" of Exhibitor type
}, {
  title: 'Stand number',
  id: exhibitorColumns.standNumber,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.standNumber, 'text'),
}, {
  title: 'Stand notes',
  id: exhibitorColumns.standNotes,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.standNotes, 'text'),
}, */ {
  title: 'Details confirmed',
  id: exhibitorColumns.detailsConfirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.detailsConfirmed, 'percent'),
}, {
  title: 'Profile webform',
  id: exhibitorColumns.editingUrlStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.editingUrlStatus, 'percent'),
}, {
  title: 'Rooms/locations',
  id: exhibitorColumns.program,
  templateId: tableTemplateIds.tableText,
  // sortingFunction: twoDeepTextSorter(exhibitorColumns.program, 'text'),
}, {
  title: 'Exhibitor contact name',
  id: exhibitorColumns.contactName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.contactName, 'text'),
}, {
  title: 'Exhibitor contact email',
  id: exhibitorColumns.contactEmail,
  templateId: tableTemplateIds.tableEmail,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.contactEmail, 'text'),
}, {
  title: 'Exhibitor contact number',
  id: exhibitorColumns.contactNumber,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.contactNumber, 'text'),
}, {
  title: 'Exhibitor contact address',
  id: exhibitorColumns.address,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.address, 'text'),
}, {
  title: 'Description',
  id: exhibitorColumns.description,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.description, 'text'),
}, {
  title: 'Call to action text',
  id: exhibitorColumns.callToActionText,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.callToActionText, 'text'),
}, {
  title: 'Call to action URL',
  id: exhibitorColumns.callToActionUrl,
  templateId: tableTemplateIds.tableLink,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.callToActionUrl, 'url'),
}, {
  title: 'Logo',
  id: exhibitorColumns.logo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Social',
  id: exhibitorColumns.social,
  templateId: tableTemplateIds.tableSocial,
  sortingFunction: arrayLengthSorter(exhibitorColumns.social)
}, {
  title: 'Exhibitor labels',
  id: exhibitorColumns.labels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(exhibitorColumns.labels)
}, {
  title: 'Exhibitor brief URL',
  id: exhibitorColumns.briefUrl,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.briefUrl, 'percent'),
}, {
  title: 'Exhibitor brief actions',
  id: exhibitorColumns.briefActions,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.briefActions, 'percent'),
}, {
  title: 'Workforce contact',
  id: exhibitorColumns.workforceContact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(exhibitorColumns.workforceContact, 'firstName'),
}, {
  title: 'Terms',
  id: exhibitorColumns.terms,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.terms, 'percent'),
}, {
  title: 'Show in publish',
  id: exhibitorColumns.hideInPublish,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(exhibitorColumns.hideInPublish, 'percent'),
}
]

const extraTemplateColumns: TableColumn[] = [{
  title: 'Workforce contact email',
  id: templateTableColumns.WFEmail,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(templateTableColumns.WFEmail, 'text'),
}, {
  title: 'Source Event Name',
  id: templateTableColumns.eventName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(templateTableColumns.eventName, 'text'),
}, {
  title: 'Creation date',
  id: templateTableColumns.createdDate,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(templateTableColumns.createdDate, 'date'),
}, {
  title: 'Last modified',
  id: templateTableColumns.modifiedDate,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(templateTableColumns.modifiedDate, 'date'),
}]

export const allDefaultExhibitorClipboardColumns: TableColumn[] = allDefaultExhibitorsColumns.filter(c => !excludedExhibitorClipboardColumns.includes(c.id as any))
allDefaultExhibitorClipboardColumns.splice(1, 0, ...extraTemplateColumns) // insert WF Contact after Name column (at index 1)

export const allDefaultSponsorsColumns: TableColumn[] = [{
  title: 'Sponsor name',
  id: sponsorColumns.name,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.name, 'text'),
}, {
  title: 'Sponsor status',
  id: sponsorColumns.sponsorStatus,
  templateId: tableTemplateIds.tableLightsMap,
  sortingFunction: ({ a, b, mult, parentId, prev }) => twoDeepNoNullSorter(sponsorColumns.sponsorStatus, parentId)({ a, b, mult, prev }),
  notOuter: true,
}, {
  title: 'Stars',
  id: sponsorColumns.stars,
  templateId: tableTemplateIds.tableStars,
  sortingFunction: twoDeepSimpleSorter(sponsorColumns.stars, 'stars'),
}, {
  title: 'Sponsor assets',
  id: sponsorColumns.assets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.assets, 'percent'),
  notNested: true,
}, {
  title: 'Session assets',
  id: sponsorColumns.sessionAssets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.sessionAssets, 'percent'),
  notNested: true,
}, {
  title: 'Profile status',
  id: sponsorColumns.detailsConfirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.detailsConfirmed, 'percent'),
}, {
  title: 'Profile webform',
  id: sponsorColumns.editingUrlStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.editingUrlStatus, 'percent'),
}, {
  title: 'Session status',
  id: sponsorColumns.sessionSummary,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.sessionSummary, 'percent'),
  notNested: true,
}, {
  title: 'Description',
  id: sponsorColumns.description,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.description, 'text'),
}, {
  title: 'Call to action text',
  id: sponsorColumns.callToActionText,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.callToActionText, 'text'),
}, {
  title: 'Call to action URL',
  id: sponsorColumns.callToActionUrl,
  templateId: tableTemplateIds.tableLink,
  sortingFunction: twoDeepTextSorter(sponsorColumns.callToActionUrl, 'url'),
}, {
  title: 'Logo',
  id: sponsorColumns.logo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Social',
  id: sponsorColumns.social,
  templateId: tableTemplateIds.tableSocial,
  sortingFunction: arrayLengthSorter(sponsorColumns.social)
}, {
  title: 'Sponsor contact name',
  id: sponsorColumns.contactName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.contactName, 'text'),
}, {
  title: 'Sponsor contact email',
  id: sponsorColumns.contactEmail,
  templateId: tableTemplateIds.tableEmail,
  sortingFunction: twoDeepTextSorter(sponsorColumns.contactEmail, 'text'),
}, {
  title: 'Sponsor contact phone',
  id: sponsorColumns.contactNumber,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.contactNumber, 'text'),
}, {
  title: 'Sponsor contact address',
  id: sponsorColumns.address,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.address, 'text'),
}, {
  title: 'Sponsor type',
  id: sponsorColumns.sponsorType,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(sponsorColumns.sponsorType, 'rank'), // sort by "rank" of sponsor type
}, {
  title: 'Sponsor labels',
  id: sponsorColumns.labels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(sponsorColumns.labels)
}, {
  title: 'Workforce contact',
  id: sponsorColumns.workforceContact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(sponsorColumns.workforceContact, 'firstName'),
}, {
  title: 'Sessions',
  id: sponsorColumns.sessionCount,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorColumns.sessionCount, 'text'),
}, {
  title: 'Show in publish',
  id: sponsorColumns.hideInPublish,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorColumns.hideInPublish, 'percent'),
},
]

export const allDefaultSponsorClipboardColumns: TableColumn[] = allDefaultSponsorsColumns.filter(c => !excludedSponsorClipboardColumns.includes(c.id as any))
allDefaultSponsorClipboardColumns.splice(1, 0, ...extraTemplateColumns) // insert WF Contact after Name column (at index 1)


export const allDefaultSponsorSessionsColumns: TableColumn[] = [{
  title: 'Room/Location name',
  id: sponsorSessionColumns.programName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorSessionColumns.programName, 'text'),
}, {
  title: 'Session title',
  id: sponsorSessionColumns.title,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(sponsorSessionColumns.title, 'text'),
}, {
  title: 'Sponsor status',
  id: sponsorSessionColumns.sponsorStatus,
  templateId: tableTemplateIds.tableLightsMap,
  sortingFunction: ({ a, b, mult, parentId, prev }) => twoDeepNoNullSorter(sponsorSessionColumns.sponsorStatus, parentId)({ a, b, mult, prev }),
  notOuter: true,
}, {
  title: 'Sponsor summary',
  id: sponsorSessionColumns.sponsorSummary,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sponsorSessionColumns.sponsorSummary, 'percent'),
  notNested: true,
}, {
  title: 'Time & date',
  extraColumnData: [{ columnName: 'Start time', id: 'startTime' }, { columnName: 'Duration', id: 'duration' }, { columnName: 'End time', id: 'endTime' }, { columnName: 'Date', id: 'date' }], 
  id: sponsorSessionColumns.time,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(sponsorSessionColumns.time, 'date'),
}, {
  title: 'Session description',
  id: sponsorSessionColumns.description,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sponsorSessionColumns.description, 'text'),
}, {
  title: 'Sponsors',
  id: sponsorSessionColumns.sponsorCount,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sponsorSessionColumns.sponsorCount, 'text'),
}, {
  title: 'Session labels',
  id: sponsorSessionColumns.sessionLabels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(sponsorSessionColumns.sessionLabels)
}, {
  title: 'Room/Location labels',
  id: sponsorSessionColumns.programLabels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(sponsorSessionColumns.programLabels)
}
]

export const allDefaultProgramColumns: TableColumn[] = [
  {
    title: 'Room/Location name',
    id: programColumns.name,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.name, 'text'),
  }, {
    title: 'Venue location',
    id: programColumns.venueLocation,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.venueLocation, 'text'),
  }, {
    title: 'Venue address',
    id: programColumns.venueAddress,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.venueAddress, 'text'),
  }, {
    title: 'Time zone',
    id: programColumns.timeZone,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.timeZone, 'text'),
  }, {
    title: 'Room/Location assets',
    id: programColumns.assets,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepTextSorter(programColumns.assets, 'text'),
  }, {
    title: 'Expected numbers',
    id: programColumns.expectedNumbers,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.expectedNumbers, 'text'),
  }, {
    title: 'Set up',
    id: programColumns.setup,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.setup, 'text'),
  }, {
    title: 'Set up notes',
    id: programColumns.setupNotes,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.setupNotes, 'text'),
  }, {
    title: 'Files',
    id: programColumns.files,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(programColumns.files, 'text'),
  }, {
    title: 'Room/Location labels',
    id: programColumns.labels,
    templateId: tableTemplateIds.tableLabels,
    sortingFunction: arrayLengthSorter(programColumns.labels)
  }
]

export const allDefaultSessionsColumns: TableColumn[] = [{
  title: 'Room/Location name',
  id: sessionColumns.programName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sessionColumns.programName, 'text'),
}, {
  title: 'Time & date',
  extraColumnData: [{ columnName: 'Start time', id: 'startTime' }, { columnName: 'Duration', id: 'duration' },  { columnName: 'End time', id: 'endTime' }, { columnName: 'Date', id: 'date' }],  
  id: sessionColumns.dateTime,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(sessionColumns.dateTime, 'date'),
}, {
  title: 'Flag',
  hideTitle: true,
  newInsertIndex: 2, 
  id: sessionColumns.flagged,
  templateId: tableTemplateIds.tableFlag,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.flagged, 'flagged'),
}, {
  title: 'Session title',
  id: sessionColumns.name,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(sessionColumns.name, 'text'),
}, {
  title: 'Duration',
  id: sessionColumns.duration,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.duration,''),
}, {
  title: 'Attendees',
  id: sessionColumns.attendees,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.attendees,''),
}, {
  title: 'Session assets',
  id: sessionColumns.assets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.assets, 'percent'),
  notNested: true,
}, {
  title: 'Performer assets',
  id: sessionColumns.performerAssets,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.performerAssets, 'percent'),
  notNested: true,
}, {
  title: 'Sponsor assets',
  id: sessionColumns.sponsorAssets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.sponsorAssets, 'percent'),
  notNested: true,
}, {
  title: 'Staging requests',
  id: sessionColumns.stagingRequests,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.stagingRequests, 'percent'),
  notNested: true,
}, {
  title: 'Performer has been briefed',
  id: sessionColumns.briefed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.briefed, 'percent'),
  notNested: true,
}, {
  title: 'Performer status',
  id: sessionColumns.performerStatus,
  templateId: tableTemplateIds.tableLightsMap,
  sortingFunction: ({ a, b, mult, parentId, prev }) => twoDeepNoNullSorter(sessionColumns.performerStatus, parentId)({ a, b, mult, prev }),
  notOuter: true,
}, {
  title: 'Invite status',
  id: sessionColumns.summary,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.summary, 'percent'),
  notNested: true,
}, {
  title: 'Session content status',
  id: sessionColumns.confirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.confirmed, 'percent'),
}, {
  title: 'Session description',
  id: sessionColumns.description,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(sessionColumns.description, 'text'),
}, {
  title: 'Performers',
  id: sessionColumns.performers,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.performers, 'text'),
}, {
  title: 'Sponsors',
  id: sessionColumns.sponsors,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.sponsors, 'text'),
}, {
  title: 'Session web form for editing',
  id: sessionColumns.sessionUrl,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.sessionUrl, 'percent'),
  notNested: true,
}, {
  title: 'Public links',
  id: sessionColumns.publicLinks,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.publicLinks, 'text'),
}, {
  title: 'Notes for performer brief',
  id: sessionColumns.briefNotes,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.briefNotes, 'text'),
}, {
  title: 'Staging Notes',
  id: sessionColumns.stagingNotes,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.stagingNotes, 'text'),
}, {
  title: 'Session labels',
  id: sessionColumns.sessionLabels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(sessionColumns.sessionLabels)
}, {
  title: 'Room/Location labels',
  id: sessionColumns.programLabels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(sessionColumns.programLabels)
}, {
  title: 'Show in publish',
  id: sessionColumns.showInPublish,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(sessionColumns.showInPublish, 'percent'),
}
]



export const allDefaultPerformerColumns: TableColumn[] = [{
  title: 'Performer name',
  id: performerTableColumns.name,
  templateId: tableTemplateIds.tableUser,
  extraColumnData: [{ columnName: 'Performer Photo URL', id: 'photo' }],
  sortingFunction: twoDeepTextSorter(performerTableColumns.name, 'name'),
}, {
  title: 'Performer type',
  id: performerTableColumns.performerType,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(performerTableColumns.performerType, 'rank'), // sort by "rank" of sponsor type
}, {
  title: 'Stars',
  id: performerTableColumns.stars,
  templateId: tableTemplateIds.tableStars,
  sortingFunction: twoDeepSimpleSorter(performerTableColumns.stars, 'stars'),
}, {
  title: 'Sessions',
  id: performerTableColumns.sessions,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.sessions, 'text'),
}, {
  title: 'Invite status',
  id: performerTableColumns.status,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.status, 'percent'),
  notNested: true,
}, {
  title: 'Performer assets',
  id: performerTableColumns.assets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.assets, 'percent'),
  notNested: true,
}, {
  title: 'Session assets',
  id: performerTableColumns.sessionAssets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.sessionAssets, 'percent'),
  notNested: true,
}, {
  title: 'Profile status',
  id: performerTableColumns.confirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.confirmed, 'percent'),
}, {
  title: 'Briefing status',
  id: performerTableColumns.briefed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.briefed, 'percent'),
}, {
  title: 'Profile webform',
  id: performerTableColumns.publicEditProfileStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.publicEditProfileStatus, 'percent'),
}, {
  title: 'Workforce contact',
  id: performerTableColumns.contact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(performerTableColumns.contact, 'firstName'),
}, {
  title: 'Contact name',
  id: performerTableColumns.contactName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.contactName, 'text'),
}, {
  title: 'Email',
  id: performerTableColumns.email,
  templateId: tableTemplateIds.tableEmail,
  sortingFunction: twoDeepTextSorter(performerTableColumns.email, 'text'),
}, {
  title: 'Phone',
  id: performerTableColumns.phone,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.phone, 'text'),
}, {
  title: 'Line 1',
  id: performerTableColumns.line1,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.line1, 'text'),
}, {
  title: 'Line 2',
  id: performerTableColumns.line2,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.line2, 'text'),
}, {
  title: 'Address',
  id: performerTableColumns.address,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.address, 'text'),
}, {
  title: 'Photo',
  id: performerTableColumns.photo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Logo',
  id: performerTableColumns.logo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Social',
  id: performerTableColumns.social,
  templateId: tableTemplateIds.tableSocial,
  sortingFunction: arrayLengthSorter(performerTableColumns.social)
}, {
  title: 'Bio',
  id: performerTableColumns.bio,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(performerTableColumns.bio, 'text'),
}, {
  title: 'Performer labels',
  id: performerTableColumns.labels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(performerTableColumns.labels)
}, {
  title: 'Performer brief URL',
  id: performerTableColumns.briefUrl,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.briefUrl, 'percent'),
}, {
  title: 'Performer brief actions',
  id: performerTableColumns.briefActions,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.briefActions, 'percent'),
}, {
  title: 'Terms',
  id: performerTableColumns.terms,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.terms, 'percent'),
}, {
  title: 'Show in publish',
  id: performerTableColumns.hideInPublish,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(performerTableColumns.hideInPublish, 'percent'),
}
]

export const allDefaultAttendeesColumns: TableColumn[] = [{
  title: 'Attendee name',
  id: attendeeTableColumns.name,
  templateId: tableTemplateIds.tableUser,
  extraColumnData: [{ columnName: 'Attendee Photo URL', id: 'photo' }],
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.name, 'name'),
}, {
  title: 'Attendee type',
  id: attendeeTableColumns.attendeeType,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(attendeeTableColumns.attendeeType, 'rank'), // sort by "rank" of sponsor type
}, {
  title: 'Sessions',
  id: attendeeTableColumns.sessions,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.sessions, 'text'),
}, {
  title: 'Confirmed',
  id: attendeeTableColumns.status,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.status, 'percent'),
}, {
  title: 'Profile webform',
  id: attendeeTableColumns.publicEditProfileStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.publicEditProfileStatus, 'percent'),
}, {
  title: 'Workforce contact',
  id: attendeeTableColumns.contact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.contact, 'firstName'),
}, {
  title: 'Email',
  id: attendeeTableColumns.email,
  templateId: tableTemplateIds.tableEmail,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.email, 'text'),
}, {
  title: 'Phone',
  id: attendeeTableColumns.phone,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.phone, 'text'),
}, {
  title: 'Line 1',
  id: attendeeTableColumns.line1,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.line1, 'text'),
}, {
  title: 'Line 2',
  id: attendeeTableColumns.line2,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.line2, 'text'),
}, {
  title: 'Address',
  id: attendeeTableColumns.address,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(attendeeTableColumns.address, 'text'),
}, {
  title: 'Photo',
  id: attendeeTableColumns.photo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Social',
  id: attendeeTableColumns.social,
  templateId: tableTemplateIds.tableSocial,
  sortingFunction: arrayLengthSorter(attendeeTableColumns.social)
}, {
  title: 'Attendee labels',
  id: attendeeTableColumns.labels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(attendeeTableColumns.labels)
}, {
  title: 'Attendee brief URL',
  id: attendeeTableColumns.briefUrl,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.briefUrl, 'percent'),
}, {
  title: 'Attendee brief actions',
  id: attendeeTableColumns.briefActions,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.briefActions, 'percent'),
}, {
  title: 'Terms',
  id: attendeeTableColumns.terms,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(attendeeTableColumns.terms, 'percent'),
}
]

export const allDefaultAttendeesClipboardColumns: TableColumn[] = allDefaultAttendeesColumns.filter(c => !excludedAttendeeClipboardColumns.includes(c.id as any))
allDefaultAttendeesClipboardColumns.splice(1, 0, ...extraTemplateColumns) // insert WF Contact after Name column (at index 1)


export const allDefaultPerformerClipboardColumns: TableColumn[] = allDefaultPerformerColumns.filter(c => !excludedPerformerClipboardColumns.includes(c.id as any))
allDefaultPerformerClipboardColumns.splice(1, 0, ...extraTemplateColumns) // insert WF Contact after Name column (at index 1)





export const allDefaultAssetColumns: TableColumn[] = [
  {
    title: 'Flag',
    hideTitle: true,
    id: assetColumns.flagged,
    templateId: tableTemplateIds.tableFlag,
    sortingFunction: twoDeepNoNullSorter(assetColumns.flagged, 'flagged'),
  }, {
    title: 'Icon',
    hideTitle: true,
    id: assetColumns.icon,
    templateId: tableTemplateIds.iconText,
    sortingFunction: twoDeepTextSorter(assetColumns.icon, 'icon'),
  }, {
    title: 'Asset category',
    id: assetColumns.category,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(assetColumns.category, 'text'),
  }, {
    title: 'Asset name',
    id: assetColumns.name,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(assetColumns.name, 'text'),
  }, {
    title: 'Qty',
    id: assetColumns.quantity,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepNoNullSorter(assetColumns.quantity, 'text'),
  }, {
    title: 'Labels',
    id: assetColumns.labels,
    templateId: tableTemplateIds.tableLabels,
    sortingFunction: arrayLengthSorter(assetColumns.labels)
  }, {
    title: 'Due Date',
    id: assetColumns.dueBy,
    templateId: tableTemplateIds.tableDate,
    sortingFunction: twoDeepSimpleSorter(assetColumns.dueBy, 'date'),
  }, {
    title: 'Information',
    id: assetColumns.description,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(assetColumns.description, 'text'),
  }, {
    title: 'Attached to',
    id: assetColumns.attachedTo,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepNoNullSorter(assetColumns.attachedTo, 'text'),
  }, {
    title: 'Responsibility',
    id: assetColumns.responsibility,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(assetColumns.responsibility, 'name'),
  }, {
    title: 'Asset URL',
    id: assetColumns.assetURL,
    templateId: tableTemplateIds.tableLink,
    sortingFunction: twoDeepTextSorter(assetColumns.assetURL, 'url'),
  }, {
    title: 'Editing URL',
    id: assetColumns.editingURL,
    templateId: tableTemplateIds.tableLink,
    sortingFunction: twoDeepTextSorter(assetColumns.editingURL, 'url'),
  }, {
    title: 'Status',
    id: assetColumns.ready,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(assetColumns.ready, 'percent'),
  }, {
    title: 'Used',
    id: assetColumns.used,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepNoNullSorter(assetColumns.used, 'text'),
  }, {
    title: 'Linked\nTo Budget',
    id: assetColumns.linked,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepNoNullSorter(assetColumns.linked, 'text'),
  }, {
    title: 'Tasks',
    id: assetColumns.tasks,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(assetColumns.tasks, 'percent'),
  }, {
    title: 'Associated With',
    id: assetColumns.associatedWith,
    templateId: tableTemplateIds.tableEntity,
    sortingFunction: twoDeepTextSorter(assetColumns.associatedWith, 'name'),
  }, {
    title: 'Created By',
    id: assetColumns.creator,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(assetColumns.creator, 'name'),
  }
]


export const allDefaultAssetClipboardColumns: TableColumn[] = [...allDefaultAssetColumns.filter(c => !excludedAssetClipboardColumns.includes(c.id as any)), ...extraTemplateColumns.filter(c => c.id !== templateTableColumns.WFEmail)]

export const allDefaultTaskColumns: TableColumn[] = [
  {
    title: 'Flag',
    hideTitle: true,
    id: taskTableColumns.flagged,
    templateId: tableTemplateIds.tableFlag,
    sortingFunction: twoDeepNoNullSorter(taskTableColumns.flagged, 'flagged'),
  }, {
    title: 'Status',
    id: taskTableColumns.status,
    templateId: tableTemplateIds.taskStatus,
    sortingFunction: taskStatusSorter,
  }, {
    title: 'Comments',
    hideTitle: true,
    id: taskTableColumns.commented,
    templateId: tableTemplateIds.tableComments,
    sortingFunction: twoDeepNoNullSorter(taskTableColumns.commented, 'comments'),
  }, {
    title: 'Task',
    id: taskTableColumns.task,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(taskTableColumns.task, 'text'),
  }, {
    title: 'Associated With',
    id: taskTableColumns.associatedWith,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(taskTableColumns.associatedWith, 'text'),
  }, {
    title: 'Event',
    id: taskTableColumns.event,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(taskTableColumns.event, 'text'),
  }, {
    title: 'Account',
    id: taskTableColumns.account,
    templateId: tableTemplateIds.tableAccount,
    sortingFunction: twoDeepTextSorter(eventListTableColumns.account, 'name'),
  }, {
    title: 'Responsibility',
    id: taskTableColumns.responsibility,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(taskTableColumns.responsibility, 'name'),
  }, {
    title: 'Due Date',
    id: taskTableColumns.dueBy,
    templateId: tableTemplateIds.tableDate,
    sortingFunction: twoDeepSimpleSorter(taskTableColumns.dueBy, 'date'),
  }, {
    title: 'Additional Information',
    id: taskTableColumns.additionalInfo,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(taskTableColumns.additionalInfo, 'text'),
  }, {
    title: 'Subtasks',
    id: taskTableColumns.subTasks,
    templateId: tableTemplateIds.tableTextLights,
    sortingFunction: twoDeepNoNullSorter(taskTableColumns.subTasks, 'percent'),
  }, {
    title: 'Visibility',
    id: taskTableColumns.visibility,
    templateId: tableTemplateIds.iconText,
    sortingFunction: twoDeepNoNullSorter(taskTableColumns.visibility, 'rank'),
  }, {
    title: 'Room/Location',
    id: taskTableColumns.program,
    templateId: tableTemplateIds.tableText,
    sortingFunction: twoDeepTextSorter(taskTableColumns.program, 'text'),
  }, {
    title: 'Labels',
    id: taskTableColumns.labels,
    templateId: tableTemplateIds.tableLabels,
    sortingFunction: arrayLengthSorter(taskTableColumns.labels)
  },  {
    title: 'Files',
    id: taskTableColumns.files,
    templateId: tableTemplateIds.tableFiles,
    sortingFunction: twoDeepNoNullSorter(taskTableColumns.files, 'files'),
  }, {
    title: 'Created By',
    id: taskTableColumns.creator,
    templateId: tableTemplateIds.tableUser,
    sortingFunction: twoDeepTextSorter(taskTableColumns.creator, 'name'),
  }
]

export const defaultTaskColumns = allDefaultTaskColumns.filter(c => ![taskTableColumns.event as string, taskTableColumns.account as string].includes(c.id))



export const allDefaultQuotesColumns: TableColumn[] = [{
  title: 'Quote Status',
  id: quoteColumns.quoteStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(quoteColumns.quoteStatus, 'percent'),
}, {
  title: 'Resource',
  id: quoteColumns.resource,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(quoteColumns.resource, 'text'),
}, {
  title: 'Section name',
  id: quoteColumns.name,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(quoteColumns.name, 'text'),
}, {
  title: 'Quote link',
  id: quoteColumns.link,
  templateId: tableTemplateIds.tableLink,
  sortingFunction: twoDeepTextSorter(quoteColumns.link, 'url'),
}, {
  title: 'Quote notes',
  id: quoteColumns.notes,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(quoteColumns.notes, 'text'),
}, {
  title: 'Service type',
  id: quoteColumns.type,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(quoteColumns.type, 'text'),
}, {
  title: 'Link Status',
  id: quoteColumns.status,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(quoteColumns.status, 'percent'),
}, {
  title: 'Quote url for editing',
  id: quoteColumns.url,
  templateId: tableTemplateIds.tableLink,
  sortingFunction: twoDeepTextSorter(quoteColumns.url, 'url'),
}, {
  title: 'Deadline',
  id: quoteColumns.deadline,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(quoteColumns.deadline, 'date'),
}, {
  title: 'Submitted',
  id: quoteColumns.submitted,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(quoteColumns.submitted, 'date'),
}, {
  title: 'Total',
  id: quoteColumns.total,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(quoteColumns.total, 'text'),
}, {
  title: 'Disc $',
  id: quoteColumns.discountAmount,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(quoteColumns.discountAmount, 'text'),
}, {
  title: 'Workforce contact',
  id: quoteColumns.workforceContact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(quoteColumns.workforceContact, 'firstName'),
}, 
]


export const allDefaultAccountQuotesColumns = [...allDefaultQuotesColumns]
allDefaultAccountQuotesColumns.splice(2, 0, {
  title: 'Event name',
  id: quoteColumns.eventName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(quoteColumns.eventName, 'text'),
})


export const allDefaultResourcesColumns: TableColumn[] = [{
  title: 'Resource name',
  id: resourceColumns.name,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(resourceColumns.name, 'text'),
}, {
  title: 'Resource type',
  id: resourceColumns.resourceType,
  templateId: tableTemplateIds.tableText, // display template only. no need to display something more than text (for now)
  sortingFunction: twoDeepSimpleSorter(resourceColumns.resourceType, 'rank'), // sort by "rank" of sponsor type
}, {
  title: 'Stars',
  id: resourceColumns.stars,
  templateId: tableTemplateIds.tableStars,
  sortingFunction: twoDeepSimpleSorter(resourceColumns.stars, 'stars'),
}, {
  title: 'Resource confirmed',
  id: resourceColumns.detailsConfirmed,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(resourceColumns.detailsConfirmed, 'percent'),
}, {
  title: 'Resource assets',
  id: resourceColumns.assets,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(resourceColumns.assets, 'percent'),
  notNested: true,
}, {
  title: 'Profile webform',
  id: resourceColumns.editingUrlStatus,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(resourceColumns.editingUrlStatus, 'percent'),
}, {
  title: 'Description',
  id: resourceColumns.description,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(resourceColumns.description, 'text'),
}, {
  title: 'Logo',
  id: resourceColumns.logo,
  templateId: tableTemplateIds.tableImage,
}, {
  title: 'Social',
  id: resourceColumns.social,
  templateId: tableTemplateIds.tableSocial,
  sortingFunction: arrayLengthSorter(resourceColumns.social)
}, {
  title: 'Primary contact',
  id: resourceColumns.contactName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(resourceColumns.contactName, 'text'),
}, {
  title: 'Contact email',
  id: resourceColumns.contactEmail,
  templateId: tableTemplateIds.tableEmail,
  sortingFunction: twoDeepTextSorter(resourceColumns.contactEmail, 'text'),
}, {
  title: 'Contact phone',
  id: resourceColumns.contactNumber,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(resourceColumns.contactNumber, 'text'),
}, {
  title: 'Resource labels',
  id: resourceColumns.labels,
  templateId: tableTemplateIds.tableLabels,
  sortingFunction: arrayLengthSorter(resourceColumns.labels)
}, {
  title: 'Workforce contact',
  id: resourceColumns.workforceContact,
  templateId: tableTemplateIds.tableWorker,
  sortingFunction: twoDeepTextSorter(resourceColumns.workforceContact, 'firstName'),
}, {
  title: 'Files',
  id: resourceColumns.files,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(resourceColumns.files, 'text'),
}, {
  title: 'Terms',
  id: resourceColumns.terms,
  templateId: tableTemplateIds.tableTextLights,
  sortingFunction: twoDeepNoNullSorter(resourceColumns.terms, 'percent'),
}
]

export const allDefaultBudgetColumns: TableColumn[] = [{
  title: 'Event Name',
  id: budgetColumns.eventName,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(budgetColumns.eventName, 'text'),
}, {
  title: 'Event Start Date',
  id: budgetColumns.startDate,
  templateId: tableTemplateIds.tableDate,
  sortingFunction: twoDeepSimpleSorter(budgetColumns.startDate, 'date'),
}, {
  title: 'Event Status',
  id: budgetColumns.eventStatus,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(budgetColumns.eventStatus, 'text'),
}, {
  title: 'Currency',
  id: budgetColumns.currency,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.currency, 'text'),
}, {
  title: 'Revenue',
  id: budgetColumns.revenue,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.revenue, 'text'),
}, {
  title: 'Tax Type',
  id: budgetColumns.taxType,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.taxType, 'text'),
}, {
  title: 'Costs',
  id: budgetColumns.costs,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.costs, 'text'),
}, {
  title: 'Margin',
  id: budgetColumns.profit,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.profit, 'text'),
}, {
  title: 'Margin %',
  id: budgetColumns.profitPercent,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextOrNumberSorter(budgetColumns.profitPercent, 'text'),
}, 
]

export const budgetColumnsById = makeMapFromListOfObjectsAndKey(allDefaultBudgetColumns, 'id')


export const allDefaultResourceClipboardColumns: TableColumn[] = allDefaultResourcesColumns.filter(c => !excludedResourceClipboardColumns.includes(c.id as any))
allDefaultResourceClipboardColumns.splice(1, 0, ...extraTemplateColumns) // insert WF Contact after Name column (at index 1)


export interface DefaultColumnSelectData { value: string, newInsertIndex?: number, selected: boolean }
export const selectedColumnsMapper = (defaultColumns: TableColumn[]): DefaultColumnSelectData[] => defaultColumns.map(({ id, newInsertIndex }, idx) => ({ value: id, selected: true, newInsertIndex: newInsertIndex || idx }))
export const titleColumnsMapper = <C extends { id: string, title: string }>(defaultColumns: C[]) => 
  makeMapFromListOfObjectsAndKeyToProperty(defaultColumns, 'id', 'title')



export const assetSelectedColumns = selectedColumnsMapper(allDefaultAssetColumns)
export const assetColumnTitles = titleColumnsMapper(allDefaultAssetColumns)
export const assetColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAssetColumns, 'id')

export const allTaskSelectedColumns = selectedColumnsMapper(allDefaultTaskColumns)
export const allTaskColumnTitles = titleColumnsMapper(allDefaultTaskColumns)
export const allTaskColumnsById = makeMapFromListOfObjectsAndKey(allDefaultTaskColumns, 'id')

export const taskSelectedColumns = selectedColumnsMapper(defaultTaskColumns)
export const taskColumnTitles = titleColumnsMapper(defaultTaskColumns)
export const taskColumnsById = makeMapFromListOfObjectsAndKey(defaultTaskColumns, 'id')

export const taskTimeSelectedColumns = selectedColumnsMapper(allDefaultTaskTimeColumns)
export const taskTimeColumnTitles = titleColumnsMapper(allDefaultTaskTimeColumns)
export const taskTimeColumnsById = makeMapFromListOfObjectsAndKey(allDefaultTaskTimeColumns, 'id')

export const timeLogSelectedColumns = selectedColumnsMapper(allDefaultTimeLogColumns)
export const timeLogColumnTitles = titleColumnsMapper(allDefaultTimeLogColumns)
export const timeLogColumnsById = makeMapFromListOfObjectsAndKey(allDefaultTimeLogColumns, 'id')

export const publishTableSelectedColumns = selectedColumnsMapper(allDefaultPublishColumns)
export const publishTableColumnTitles = titleColumnsMapper(allDefaultPublishColumns)
export const publishTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultPublishColumns, 'id')

export const eventListTableSelectedColumns = selectedColumnsMapper(allDefaultEventListColumns)
export const eventListTableColumnTitles = titleColumnsMapper(allDefaultEventListColumns)
export const eventListTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultEventListColumns, 'id')

export const exhibitorSelectedColumns = selectedColumnsMapper(allDefaultExhibitorsColumns)
export const exhibitorColumnTitles = titleColumnsMapper(allDefaultExhibitorsColumns)
export const exhibitorColumnsById = makeMapFromListOfObjectsAndKey(allDefaultExhibitorsColumns, 'id')

export const exhibitorStandSelectedColumns = selectedColumnsMapper(allDefaultExhibitorStandsColumns)
export const exhibitorStandColumnTitles = titleColumnsMapper(allDefaultExhibitorStandsColumns)
export const exhibitorStandColumnsById = makeMapFromListOfObjectsAndKey(allDefaultExhibitorStandsColumns, 'id')

export const exhibitorTemplateSelectedColumns = selectedColumnsMapper(allDefaultExhibitorClipboardColumns)
export const exhibitorTemplateColumnTitles = titleColumnsMapper(allDefaultExhibitorClipboardColumns)
export const exhibitorTemplateColumnsById = makeMapFromListOfObjectsAndKey(allDefaultExhibitorClipboardColumns, 'id')

export const assetTemplateSelectedColumns = selectedColumnsMapper(allDefaultAssetClipboardColumns)
export const assetTemplateColumnTitles = titleColumnsMapper(allDefaultAssetClipboardColumns)
export const assetTemplateColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAssetClipboardColumns, 'id')

export const sponsorSelectedColumns = selectedColumnsMapper(allDefaultSponsorsColumns.filter(column => !column.notOuter))
export const sponsorColumnTitles = titleColumnsMapper(allDefaultSponsorsColumns)
export const sponsorColumnsById = makeMapFromListOfObjectsAndKey(allDefaultSponsorsColumns, 'id')

export const attendeeSelectedColumns = selectedColumnsMapper(allDefaultAttendeesColumns)
export const attendeeColumnTitles = titleColumnsMapper(allDefaultAttendeesColumns)
export const attendeeColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAttendeesColumns, 'id')

export const sponsorTemplateSelectedColumns = selectedColumnsMapper(allDefaultSponsorClipboardColumns.filter(column => !column.notOuter))
export const sponsorTemplateColumnTitles = titleColumnsMapper(allDefaultSponsorClipboardColumns)
export const sponsorTemplateColumnsById = makeMapFromListOfObjectsAndKey(allDefaultSponsorClipboardColumns, 'id')

export const resourceSelectedColumns = selectedColumnsMapper(allDefaultResourcesColumns.filter(column => !column.notOuter))
export const resourceColumnTitles = titleColumnsMapper(allDefaultResourcesColumns)
export const resourceColumnsById = makeMapFromListOfObjectsAndKey(allDefaultResourcesColumns, 'id')

export const quoteSelectedColumns = selectedColumnsMapper(allDefaultQuotesColumns.filter(column => !column.notOuter))
export const quoteColumnTitles = titleColumnsMapper(allDefaultQuotesColumns)
export const quoteColumnsById = makeMapFromListOfObjectsAndKey(allDefaultQuotesColumns, 'id')

export const accountQuoteColumnTitles = titleColumnsMapper(allDefaultAccountQuotesColumns)
export const accountQuoteColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAccountQuotesColumns, 'id')


export const resourceTemplateSelectedColumns = selectedColumnsMapper(allDefaultResourceClipboardColumns.filter(column => !column.notOuter))
export const resourceTemplateColumnTitles = titleColumnsMapper(allDefaultResourceClipboardColumns)
export const resourceTemplateColumnsById = makeMapFromListOfObjectsAndKey(allDefaultResourceClipboardColumns, 'id')

export const sponsorSessionSelectedColumns = selectedColumnsMapper(allDefaultSponsorSessionsColumns.filter(column => !column.notOuter))
export const sponsorSessionColumnTitles = titleColumnsMapper(allDefaultSponsorSessionsColumns)
export const sponsorSessionColumnsById = makeMapFromListOfObjectsAndKey(allDefaultSponsorSessionsColumns, 'id')

export const shareTableSelectedColumns = selectedColumnsMapper(allDefaultShareColumns)
export const shareTableColumnTitles = titleColumnsMapper(allDefaultShareColumns)
export const shareTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultShareColumns, 'id')

export const logisticsShareTableSelectedColumns = selectedColumnsMapper(allDefaultShareColumns)
export const logisticsShareTableColumnTitles = titleColumnsMapper(allDefaultShareColumns)
export const logisticsShareTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultShareColumns, 'id')

export const performerBriefTableSelectedColumns = selectedColumnsMapper(allDefaultPerformerBriefColumns)
export const performerBriefTableColumnTitles = titleColumnsMapper(allDefaultPerformerBriefColumns)
export const performerBriefTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultPerformerBriefColumns, 'id')

export const exhibitorBriefTableSelectedColumns = selectedColumnsMapper(allDefaultExhibitorBriefColumns)
export const exhibitorBriefTableColumnTitles = titleColumnsMapper(allDefaultExhibitorBriefColumns)
export const exhibitorBriefTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultExhibitorBriefColumns, 'id')


export const performerTableSelectedColumns = selectedColumnsMapper(allDefaultPerformerColumns.filter(column => !column.notOuter))
export const performerTableColumnTitles = titleColumnsMapper(allDefaultPerformerColumns)
export const performerTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultPerformerColumns, 'id')

export const performerTemplateTableSelectedColumns = selectedColumnsMapper(allDefaultPerformerClipboardColumns.filter(column => !column.notOuter))
export const performerTemplateTableColumnTitles = titleColumnsMapper(allDefaultPerformerClipboardColumns)
export const performerTemplateTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultPerformerClipboardColumns, 'id')

export const attendeeTemplateTableSelectedColumns = selectedColumnsMapper(allDefaultAttendeesClipboardColumns.filter(column => !column.notOuter))
export const attendeeTemplateTableColumnTitles = titleColumnsMapper(allDefaultAttendeesClipboardColumns)
export const attendeeTemplateTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultAttendeesClipboardColumns, 'id')

export const sessionsTableSelectedColumns = selectedColumnsMapper(allDefaultSessionsColumns.filter(column => !column.notOuter))
export const sessionsTableColumnTitles = titleColumnsMapper(allDefaultSessionsColumns)
export const sessionsTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultSessionsColumns, 'id')

export const programTableSelectedColumns = selectedColumnsMapper(allDefaultProgramColumns.filter(column => !column.notOuter))
export const programTableColumnTitles = titleColumnsMapper(allDefaultProgramColumns)
export const programTableColumnsById = makeMapFromListOfObjectsAndKey(allDefaultProgramColumns, 'id')

export const DefaultMaximumTableColumnWidth = 500

export const makeGridColumnTemplate = (bigSecondColumn: boolean, columnWidths: number[], checkBoxCellWidth = 50, nested = false, childrenOpen = false) => {
  const template = (checkBoxCellWidth ? `${checkBoxCellWidth}px ` : '') +
    (bigSecondColumn ? `min-content minmax(min-content, 1000fr) ${minContents(columnWidths.length - 2)}` :
      `${minContents(columnWidths.length)} ${nested || childrenOpen ? 'min-content' : '1fr'}`)
  return template
}

export interface PublishDotMenuChoices { view, edit, delete, duplicate, update }
export const publishDotMenuIdMap: AllSameKeyValuesEnforcer<PublishDotMenuChoices> = { view: 'view', edit: 'edit', delete: 'delete', duplicate: 'duplicate', update: 'update' }
export const publishDotMenuIds = new Set(Object.values(publishDotMenuIdMap).map(x => x))

export interface ShareOSSDotMenuChoices { view, edit, delete, duplicate }
export const shareOSSDotMenuIdMap: AllSameKeyValuesEnforcer<ShareOSSDotMenuChoices> = { view: 'view', edit: 'edit', delete: 'delete', duplicate: 'duplicate' }
export const shareOSSDotMenuIds = new Set(Object.values(shareOSSDotMenuIdMap).map(x => x.toString()))
